/**
 * Project Status Badge Component
 *
 * Displays a colored badge with icon for project status.
 * Supports the 5 project states defined in the system.
 *
 * @module components/common/ProjectStatusBadge
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Badge } from 'react-bootstrap';
import { PROJECT_STATUS } from '../../utils/constants';

/**
 * Status configuration map
 * Maps each project status to its display properties
 */
const STATUS_CONFIG = {
  [PROJECT_STATUS.QUOTATION_SENT]: {
    label: 'Cotización Enviada',
    variant: 'warning',
    icon: 'bi-file-text',
    description: 'Cotización enviada, esperando respuesta del cliente',
  },
  [PROJECT_STATUS.QUOTATION_ACCEPTED]: {
    label: 'Cotización Aceptada',
    variant: 'info',
    icon: 'bi-file-check',
    description: 'Cliente aceptó la cotización, esperando orden de compra',
  },
  [PROJECT_STATUS.PO_RECEIVED]: {
    label: 'PO Recibido',
    variant: 'primary',
    icon: 'bi-receipt',
    description: 'Orden de compra recibida, proyecto aprobado',
  },
  [PROJECT_STATUS.IN_PROGRESS]: {
    label: 'En Progreso',
    variant: 'success',
    icon: 'bi-play-circle',
    description: 'Proyecto en ejecución',
  },
  [PROJECT_STATUS.CLOSED]: {
    label: 'Cerrado',
    variant: 'dark',
    icon: 'bi-check-circle',
    description: 'Proyecto finalizado y cerrado',
  },
};

/**
 * ProjectStatusBadge Component
 *
 * @param {Object} props - Component props
 * @param {string} props.status - Project status (from PROJECT_STATUS constants)
 * @param {boolean} [props.showIcon=true] - Whether to show the status icon
 * @param {boolean} [props.showTooltip=false] - Whether to show tooltip with description
 * @param {string} [props.size='md'] - Badge size ('sm', 'md', 'lg')
 * @param {string} [props.className=''] - Additional CSS classes
 */
const ProjectStatusBadge = ({
  status,
  showIcon = true,
  showTooltip = false,
  size = 'md',
  className = '',
}) => {
  // Get status configuration or fallback
  const config = STATUS_CONFIG[status] || {
    label: status || 'Desconocido',
    variant: 'secondary',
    icon: 'bi-question-circle',
    description: 'Estado desconocido',
  };

  // Size classes for badge
  const sizeClasses = {
    sm: 'badge-sm',
    md: '',
    lg: 'badge-lg',
  };

  return (
    <Badge
      bg={config.variant}
      className={`${sizeClasses[size]} ${className}`}
      title={showTooltip ? config.description : undefined}
      data-testid={`project-status-badge-${status}`}
    >
      {showIcon && <i className={`bi ${config.icon} me-1`}></i>}
      {config.label}
    </Badge>
  );
};

ProjectStatusBadge.propTypes = {
  status: PropTypes.oneOf(Object.values(PROJECT_STATUS)).isRequired,
  showIcon: PropTypes.bool,
  showTooltip: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
};

export default ProjectStatusBadge;

/**
 * Helper function to get status info without rendering
 * Useful for exporting or processing status data
 *
 * @param {string} status - Project status
 * @returns {Object} Status configuration
 */
export const getStatusInfo = (status) => {
  return STATUS_CONFIG[status] || {
    label: status || 'Desconocido',
    variant: 'secondary',
    icon: 'bi-question-circle',
    description: 'Estado desconocido',
  };
};

/**
 * Get all available statuses with their configuration
 * Useful for building status filters or legends
 *
 * @returns {Array} Array of status objects
 */
export const getAllStatuses = () => {
  return Object.entries(STATUS_CONFIG).map(([value, config]) => ({
    value,
    ...config,
  }));
};
